import React, { createContext, useContext, useState, useEffect } from 'react'

import { onboardingApi } from '../../../../hooks/api/onboarding'

interface OnboardingQuestionnaireData {
  id?: string
  securityId?: string
  status?: 0 | 1 | 2 // 0:未回答, 1:回答リクエスト, 2:回答済み
  weeklyWorkDays?: number
  idealMonthlySalary?: number
  workEnvironmentPreference?: 1 | 2 | 3
  shiftTimePreference?: 1 | 2 | 3
  commuteTimeMinutes?: number
  commuteMethods?: number
  careerPositionPreference?: 1 | 2
  skillDevelopmentInterest?: boolean
  workDurationExpectation?: 1 | 2
  companyDiscoverySource?: number
}

interface OnboardingContextValue {
  questionnaire: OnboardingQuestionnaireData | null
  isLoading: boolean
  error: string | null
  fetchQuestionnaire: () => Promise<void>
  updateQuestionnaire: (data: OnboardingQuestionnaireData) => Promise<boolean>
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
)

export const useOnboarding = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider')
  }
  return context
}

interface OnboardingProviderProps {
  children: React.ReactNode
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const [questionnaire, setQuestionnaire] =
    useState<OnboardingQuestionnaireData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchQuestionnaire = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await onboardingApi().getQuestionnaire()
      setQuestionnaire(response.data)
    } catch (err) {
      setError('')
      console.error('Failed to fetch questionnaire:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const updateQuestionnaire = async (
    data: OnboardingQuestionnaireData,
  ): Promise<boolean> => {
    setIsLoading(true)
    setError(null)
    try {
      await onboardingApi().updateQuestionnaire(data)
      await fetchQuestionnaire()
      return true
    } catch (err) {
      setError('')
      console.error('Failed to update questionnaire:', err)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchQuestionnaire()
  }, [])

  const value: OnboardingContextValue = {
    questionnaire,
    isLoading,
    error,
    fetchQuestionnaire,
    updateQuestionnaire,
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}
